import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { toast } from "sonner";
import { CheckCircle, Clock, AlertCircle, ShieldCheck, User } from "lucide-react";

const initialForm = {
  full_name: "",
  date_of_birth: "",
  gender: "",
  address: "",
  state: "",
  city: "",
  pin_code: "",
  pan_number: "",
  pan_front_url: "",
  aadhaar_number: "",
  aadhaar_front_url: "",
  aadhaar_back_url: "",
  selfie_url: "",
  account_holder_name: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  upi_id: "",
};

/**
 * KYCForm - Full KYC submission form (Cashkaro-style).
 * Props:
 *   role: "user" | "seller"  → picks the endpoint
 */
const KYCForm = ({ role = "user" }) => {
  const { user, getAuthHeaders } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("pending");
  const [adminNote, setAdminNote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const endpoint = role === "seller" ? "/seller/kyc" : "/users/kyc";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}${endpoint}`, { headers: getAuthHeaders() });
        if (res.data?.kyc_status) setStatus(res.data.kyc_status);
        if (res.data?.kyc_admin_note) setAdminNote(res.data.kyc_admin_note);
        if (res.data?.kyc_data) setForm({ ...initialForm, ...res.data.kyc_data });
      } catch (e) {
        // no existing KYC
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requiredValid = () => {
    const missing = [];
    if (!form.full_name) missing.push("Full Name");
    if (!form.pan_number) missing.push("PAN Number");
    if (!form.pan_front_url) missing.push("PAN Image");
    if (!form.aadhaar_number) missing.push("Aadhaar Number");
    if (!form.aadhaar_front_url) missing.push("Aadhaar Front Image");
    if (!form.selfie_url) missing.push("Selfie");
    if (!form.account_holder_name) missing.push("Account Holder Name");
    if (!form.bank_name) missing.push("Bank Name");
    if (!form.account_number) missing.push("Account Number");
    if (!form.ifsc_code) missing.push("IFSC Code");
    return missing;
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = requiredValid();
    if (missing.length) {
      toast.error(`Please complete: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}${endpoint}`, form, { headers: getAuthHeaders() });
      toast.success("KYC submitted. Under review by admin.");
      setStatus("pending");
      setAdminNote(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit KYC");
    }
    setSubmitting(false);
  };

  const statusBadge = () => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case "under_review":
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Under Review</Badge>;
      case "resubmit":
        return <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Please Resubmit</Badge>;
      case "pending":
      default:
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  if (loading) {
    return <div className="text-center py-10 text-gray-500">Loading KYC…</div>;
  }

  const disabled = status === "approved" || status === "under_review" || submitting;

  return (
    <form onSubmit={submit} className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary-600" /> KYC Status
          </CardTitle>
          {statusBadge()}
        </CardHeader>
        {adminNote && (
          <CardContent>
            <div className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm border border-amber-100">
              <strong>Admin note:</strong> {adminNote}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Personal Details */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4 text-primary-600" /> Personal Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Full Name *</Label>
            <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} disabled={disabled} data-testid="kyc-full-name" />
          </div>
          <div>
            <Label>Date of Birth</Label>
            <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} disabled={disabled} data-testid="kyc-dob" />
          </div>
          <div>
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(v) => set("gender", v)} disabled={disabled}>
              <SelectTrigger data-testid="kyc-gender"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Address</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} disabled={disabled} data-testid="kyc-address" />
          </div>
          <div>
            <Label>State</Label>
            <Input value={form.state} onChange={(e) => set("state", e.target.value)} disabled={disabled} data-testid="kyc-state" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} disabled={disabled} data-testid="kyc-city" />
          </div>
          <div>
            <Label>PIN Code</Label>
            <Input value={form.pin_code} onChange={(e) => set("pin_code", e.target.value)} maxLength={6} disabled={disabled} data-testid="kyc-pincode" />
          </div>
        </CardContent>
      </Card>

      {/* PAN */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">PAN Card</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>PAN Number *</Label>
            <Input
              value={form.pan_number}
              onChange={(e) => set("pan_number", e.target.value.toUpperCase())}
              maxLength={10}
              placeholder="ABCDE1234F"
              disabled={disabled}
              data-testid="kyc-pan-number"
            />
          </div>
          <div>
            <Label>PAN Card Image *</Label>
            <CloudinaryUpload
              value={form.pan_front_url}
              onChange={(url) => set("pan_front_url", url)}
              folder="kyc/"
              resourceType="auto"
              accept="image/*,application/pdf"
              testId="kyc-pan-upload"
            />
          </div>
        </CardContent>
      </Card>

      {/* Aadhaar */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Aadhaar Card</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Aadhaar Number *</Label>
            <Input
              value={form.aadhaar_number}
              onChange={(e) => set("aadhaar_number", e.target.value.replace(/\D/g, ""))}
              maxLength={12}
              placeholder="12-digit Aadhaar"
              disabled={disabled}
              data-testid="kyc-aadhaar-number"
            />
          </div>
          <div>
            <Label>Aadhaar Front *</Label>
            <CloudinaryUpload
              value={form.aadhaar_front_url}
              onChange={(url) => set("aadhaar_front_url", url)}
              folder="kyc/"
              resourceType="auto"
              accept="image/*,application/pdf"
              testId="kyc-aadhaar-front-upload"
            />
          </div>
          <div>
            <Label>Aadhaar Back (Optional)</Label>
            <CloudinaryUpload
              value={form.aadhaar_back_url}
              onChange={(url) => set("aadhaar_back_url", url)}
              folder="kyc/"
              resourceType="auto"
              accept="image/*,application/pdf"
              testId="kyc-aadhaar-back-upload"
            />
          </div>
        </CardContent>
      </Card>

      {/* Selfie */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Selfie / Live Photo *</CardTitle>
        </CardHeader>
        <CardContent>
          <CloudinaryUpload
            value={form.selfie_url}
            onChange={(url) => set("selfie_url", url)}
            folder="kyc/"
            resourceType="image"
            accept="image/*"
            testId="kyc-selfie-upload"
          />
          <p className="text-xs text-gray-500 mt-2">Upload a clear photo of your face for identity verification.</p>
        </CardContent>
      </Card>

      {/* Bank Details */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Bank Details</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Account Holder Name *</Label>
            <Input value={form.account_holder_name} onChange={(e) => set("account_holder_name", e.target.value)} disabled={disabled} data-testid="kyc-account-holder" />
          </div>
          <div>
            <Label>Bank Name *</Label>
            <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} disabled={disabled} data-testid="kyc-bank-name" />
          </div>
          <div>
            <Label>Account Number *</Label>
            <Input value={form.account_number} onChange={(e) => set("account_number", e.target.value.replace(/\D/g, ""))} disabled={disabled} data-testid="kyc-account-number" />
          </div>
          <div>
            <Label>IFSC Code *</Label>
            <Input value={form.ifsc_code} onChange={(e) => set("ifsc_code", e.target.value.toUpperCase())} disabled={disabled} placeholder="e.g. HDFC0001234" data-testid="kyc-ifsc" />
          </div>
        </CardContent>
      </Card>

      {/* UPI */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">UPI ID (Optional)</CardTitle>
        </CardHeader>
        <CardContent>
          <Input value={form.upi_id} onChange={(e) => set("upi_id", e.target.value)} placeholder="yourname@upi" disabled={disabled} data-testid="kyc-upi-id" />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="submit"
          className="bg-primary-600 hover:bg-primary-700"
          disabled={disabled}
          data-testid="kyc-submit-btn"
        >
          {submitting ? "Submitting..." : status === "approved" ? "KYC Approved" : status === "under_review" ? "Under Review" : "Submit KYC"}
        </Button>
      </div>
    </form>
  );
};

export default KYCForm;
